import { useState } from "react";
import { Link } from "react-router-dom";
import Hero from "@/components/Hero";
import Marquee from "@/components/Marquee";
import ProductCard from "@/components/ProductCard";
import ProductModal from "@/components/ProductModal";
import ReviewsCarousel from "@/components/ReviewsCarousel";
import { products, categories } from "@/data/products";
import { ArrowRight, Sparkles, Award, Truck, Heart } from "lucide-react";

const ASSET_BASE = `${process.env.PUBLIC_URL || ""}/assets`;

export default function Home() {
  const [open, setOpen] = useState(null);
  const featured = ["kulfi-pista", "fp-american-nuts", "fp-real-mango", "cone-chocolate", "other-shahi-matka", "fp-kesar-pista"]
    .map((id) => products.find(p => p.id === id)).filter(Boolean);

  return (
    <>
      <Hero />

      <Marquee />

      {/* Categories preview strip */}
      <section className="py-20 md:py-28" data-testid="home-categories">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              <p className="text-xs tracking-[0.2em] uppercase text-holiday-red font-bold">Three ways to celebrate</p>
              <h2 className="font-display text-4xl md:text-5xl mt-2">From single scoops<br className="md:hidden"/> to wedding-size tubs.</h2>
            </div>
            <Link to="/products" className="hidden md:inline-flex items-center gap-2 underline underline-offset-4 font-bold">
              Browse full catalogue <ArrowRight className="w-4 h-4"/>
            </Link>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {categories.map((c, i) => {
              const tints = ["bg-ice-pink", "bg-mint", "bg-sun-yellow-soft"];
              return (
                <Link key={c.id} to="/products" data-testid={`home-category-${c.id}`} className={`group relative ${tints[i % 3]} rounded-[2rem] p-8 cushion border border-[#F0E4E4] overflow-hidden hover:-translate-y-1 transition-transform`}>
                  <span className="absolute -top-4 -right-4 text-9xl font-display text-white opacity-60 select-none">{i + 1}</span>
                  <p className="text-xs uppercase tracking-[0.18em] font-bold text-holiday-red">{c.priceHint}</p>
                  <h3 className="font-display text-3xl mt-2">{c.label}</h3>
                  <p className="font-serif italic text-sm text-choc/80 mt-2 max-w-[260px]">{c.tagline}</p>
                  <div className="mt-6 flex items-center gap-2 text-sm font-bold">
                    See range <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform"/>
                  </div>
                </Link>
              );
            })}
            {/* Decorative video tile to fill the last cell when category count isn't a multiple of 3 */}
            <div data-testid="home-categories-video-tile" className="relative rounded-[2rem] cushion border border-[#F0E4E4] overflow-hidden bg-choc">
              <video
                src={`${ASSET_BASE}/videos/scoop.mp4`}
                autoPlay loop muted playsInline preload="auto"
                poster={`${ASSET_BASE}/menu/kulfi-pista.jpg`}
                className="absolute inset-0 w-full h-full object-cover opacity-90"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-choc/70 via-choc/20 to-transparent"/>
              <div className="relative p-8 h-full min-h-[260px] flex flex-col justify-end text-cream">
                <span className="sticker bg-cream/90 text-choc self-start mb-3">Watch · 24×7 prep</span>
                <h3 className="font-display text-3xl leading-[1]">Made fresh<br/>every <span className="text-sun-yellow">hour.</span></h3>
                <p className="font-serif italic text-sm text-cream/85 mt-2 max-w-[230px]">Our kitchen never sleeps — neither does your craving.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured products */}
      <section className="py-12 md:py-20 bg-mint/40" data-testid="home-featured">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-4xl md:text-5xl mb-8">Today's tray of <span className="text-holiday-red">favourites</span>.</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-6">
            {featured.map((p) => <ProductCard key={p.id} product={p} onOpen={setOpen} />)}
          </div>
          <div className="mt-10 text-center">
            <Link to="/products" className="inline-flex items-center gap-2 bg-choc text-cream px-7 py-3.5 rounded-full font-bold hover:bg-choc/90">
              See all 20+ products <ArrowRight className="w-4 h-4"/>
            </Link>
          </div>
        </div>
      </section>

      {/* Specialty pillars */}
      <section className="py-20 md:py-28" data-testid="home-pillars">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-4xl md:text-5xl mb-12 max-w-3xl">Why Gorakhpur picks Holiday again, and again.</h2>
          <div className="grid md:grid-cols-4 gap-5">
            {[
              { icon: Award, title: "Since 1999", desc: "26 years of recipes refined by three generations of ice-cream makers." },
              { icon: Sparkles, title: "Slow-cooked", desc: "Real milk, hours of reduction. No vegetable fat shortcuts." },
              { icon: Truck, title: "Bulk catering", desc: "Weddings, birthdays, corporate — delivered chilled, on time." },
              { icon: Heart, title: "24×7 open", desc: "Late-night cravings? We're here. Every hour, every day." },
            ].map((p, i) => (
              <div key={i} className="bg-white rounded-[1.5rem] p-6 border border-[#F0E4E4] cushion">
                <div className="w-12 h-12 rounded-full bg-ice-pink flex items-center justify-center mb-4">
                  <p.icon className="w-5 h-5 text-holiday-red"/>
                </div>
                <h3 className="font-serif text-xl">{p.title}</h3>
                <p className="text-sm text-choc/75 mt-2 leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <ReviewsCarousel />

      {/* Bulk CTA banner */}
      <section className="px-4 sm:px-6 lg:px-8 mt-12">
        <div className="max-w-7xl mx-auto relative overflow-hidden rounded-[2.5rem] bg-holiday-red text-white p-10 md:p-14" data-testid="home-bulk-banner">
          <div className="grid md:grid-cols-2 gap-8 items-center relative z-10">
            <div>
              <p className="text-xs tracking-[0.2em] uppercase font-bold opacity-90">Discount on Bulk Orders</p>
              <h2 className="font-display text-4xl md:text-5xl leading-tight mt-2">Planning a wedding,<br/>a school day, a launch?</h2>
              <p className="mt-4 text-base opacity-90 max-w-md">From 5L party tubs (₹425) to 10L mix-flavour wedding packs (₹700) — call us, we'll handle the rest.</p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link to="/bulk-order" className="inline-flex items-center gap-2 bg-white text-holiday-red px-7 py-3.5 rounded-full font-bold hover:bg-cream">
                  Get a quote <ArrowRight className="w-4 h-4"/>
                </Link>
                <a href="tel:9235372759" className="inline-flex items-center gap-2 bg-choc text-cream px-7 py-3.5 rounded-full font-bold hover:bg-choc/90">
                  Call 9235372759
                </a>
              </div>
            </div>
            <div className="relative aspect-video md:aspect-[4/3] rounded-[2rem] overflow-hidden">
              <video src={`${ASSET_BASE}/videos/wedding-cake.mp4`} autoPlay loop muted playsInline className="w-full h-full object-cover"/>
            </div>
          </div>
          <div className="absolute -top-12 -right-12 w-64 h-64 rounded-full bg-sun-yellow opacity-30"/>
        </div>
      </section>

      <ProductModal product={open} onClose={() => setOpen(null)} />
    </>
  );
}

